import { Plus, Trash2 } from "@tamagui/lucide-icons";
import { uuid } from "expo-modules-core";
import type { Timestamp } from "firebase/firestore";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, XStack, YStack } from "tamagui";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Group } from "../../ui/Group";
import { Input } from "../../ui/InputNew";
import { Label } from "../../ui/Label";
import { Subtext } from "../../ui/Subtext";
import { Editor } from "../../ui/YooptaEditor";
import type { BusinessTemplateScheduleItem } from "../admin/templates/BusinessTemplateModel";
import { BusinessOffsetDateSelector } from "../modules/ui/BusinessAdminDatePicker";
import { BusinessAdminTitleAndEmojiEdit } from "../modules/ui/BusinessAdminEmojiPicker";
import type { BusinessScheduleItemSchedule } from "./BusinessScheduleItem";

const TIME_COLUMN_WIDTH = 60;

const ScheduleForm: React.FC<{
  value?: BusinessScheduleItemSchedule[] | null;
  onChange?: (items: BusinessScheduleItemSchedule[]) => void;
}> = observer(({ value, onChange }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<BusinessScheduleItemSchedule[]>(
    value ?? [{ uid: uuid.v4(), startTime: "", endTime: "", title: "" }],
  );

  return (
    <YStack gap="$medium">
      <XStack gap="$medium">
        <View flexShrink={0} width={TIME_COLUMN_WIDTH}>
          <Body>{t("business:schedule_start")}</Body>
        </View>
        <View flexShrink={0} width={TIME_COLUMN_WIDTH}>
          <Body>{t("business:schedule_end")}</Body>
        </View>
        <Body>{t("business:schedule_title")}</Body>
      </XStack>
      <Group borderBottom>
        {items.map((item, index) => (
          <XStack gap="$medium" key={item.uid}>
            <View flexShrink={0} width={TIME_COLUMN_WIDTH}>
              <Input
                placeholder="hh:mm"
                defaultValue={item.startTime}
                onChangeText={(text) =>
                  setItems((items) => {
                    items[index].startTime = text;

                    onChange?.(items);

                    return items;
                  })
                }
              />
            </View>
            <View flexShrink={0} width={TIME_COLUMN_WIDTH}>
              <Input
                placeholder="hh:mm"
                defaultValue={item.endTime}
                onChangeText={(text) =>
                  setItems((items) => {
                    items[index].endTime = text;

                    onChange?.(items);

                    return items;
                  })
                }
              />
            </View>
            <Input
              placeholder={t("business:schedule_title")}
              defaultValue={item.title}
              onChangeText={(text) =>
                setItems((items) => {
                  items[index].title = text;

                  onChange?.(items);

                  return items;
                })
              }
            />
            <View>
              <Trash2 />
            </View>
          </XStack>
        ))}
      </Group>
      <XStack
        cursor="pointer"
        gap="$small"
        alignItems="center"
        onPress={() =>
          setItems((currentItems) => {
            return currentItems.concat({
              uid: uuid.v4(),
              startTime: "",
              endTime: "",
              title: "",
            });
          })
        }
      >
        <Plus size={16} />
        <Subtext>{t("business:schedule_add_row")}</Subtext>
      </XStack>
    </YStack>
  );
});

export const BusinessScheduleItemForm: React.FC<{
  data?: BusinessTemplateScheduleItem;
  businessPlanStartingDate: Timestamp | null;
  onSave: (data: Partial<BusinessTemplateScheduleItem>) => void;
}> = observer((props) => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState<
    Partial<BusinessTemplateScheduleItem>
  >(props.data ? toJS(props.data) : {});

  return (
    <View>
      <Box gap="$medium">
        <BusinessAdminTitleAndEmojiEdit
          formValues={formValues}
          onChange={(f) =>
            setFormValues((values) => {
              values.emoji = f.emoji;
              values.title = f.title ?? "";
              return values;
            })
          }
        />

        <YStack gap="$small">
          <Label>{t("business:schedule_date")}</Label>
          <BusinessOffsetDateSelector
            startingDate={props.businessPlanStartingDate}
            offsetDays={formValues.offsetInDays ?? undefined}
            date={formValues.date ? new Date(formValues.date) : undefined}
            onChange={(value) =>
              setFormValues((values) => {
                if ("offset" in value) {
                  formValues.offsetInDays = value.offset;
                  formValues.date = null;
                } else {
                  formValues.offsetInDays = null;
                  formValues.date = value.date.toISOString().split("T")[0];
                }
                return values;
              })
            }
          />
        </YStack>

        <YStack gap="$small">
          <Label>{t("business:description")}</Label>
          <View
            paddingHorizontal={23}
            paddingVertical={12}
            borderColor="$divider"
            borderWidth={1}
          >
            <Editor
              value={formValues.description ?? undefined}
              onChange={(editor) => {
                setFormValues((values) => {
                  values.description = editor;
                  return values;
                });
              }}
              id={"description"}
              autoFocus={false}
            />
          </View>
        </YStack>

        <YStack gap="$small">
          <Label>{t("business:schedule_add")}</Label>
          <ScheduleForm
            value={formValues.schedule}
            onChange={(value) => {
              setFormValues((values) => {
                values.schedule = value;
                return values;
              });
            }}
          />
        </YStack>
      </Box>
      <Button
        onPress={() => {
          props.onSave(formValues);
        }}
      >
        {t("business:save")}
      </Button>
    </View>
  );
});
